import { ComponentFactory } from './component_factory';
import { BaseBuilder } from '../base_builder';

/**
 * @desc Build components based on schema definition
 */
class ComponentBuilder extends BaseBuilder {

    /**
     * @desc Initialise the builder instance
     * @param {Object} schemaModel - the component schema model
     * @constructor
     */
    constructor(schemaModel) {
        super(schemaModel);
        this.factory = new ComponentFactory(schemaModel);
    }

    /**
     * @desc Build Ctm or vertical specific components, also build component's children by calling director
     * @param {String} componentKey - to identify which component to build
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @returns {Node} built component with props and children filled
     */
    build = (componentKey, extraProps) => {
        /**
         * @desc you can have normal component schema or have one extend another, currently we only support extends
         * a normal component (i.e. you can't extend a component that is already extending another component )
         * @example
         * "phoneNumber_default": {
                "componentName": "PhoneNumber",
                "elementProps": {
                    "href": "tel:1300-798-697"
                },
                "contentProps": {
                    "label": "1300 798 697"
                },
                "visualProps": {
                      "color": "secondary",
                      "size": "xxxlarge"
                }
            },
         "phoneNumber_callUsNow": {
                "extends": "phoneNumber_default",
                "visualProps": {
                    "variant": "callUsNow"
                },
                "children": ["layout_xxx", "component_xxx", "form_xxx"]
            }
         */

        const componentSchema = this.schemaModel.getMergedSchema(componentKey);
        if (!componentSchema) {
            throw new Error(`Component schema for ${componentKey} is not defined. Ensure it is exported from schema/components/index.js`);
        }
        componentSchema.componentKey = componentKey;

        if (!componentSchema.componentName) {
            throw Error(`The component schema for ${componentKey} is not valid. Check that the component is exported from ctm-components and from component schema index.js.`);
        }

        return this.buildComponent(componentSchema, extraProps, componentKey);
    };

    /**
     * @desc build component defined in a single component schema
     * @param {Object} componentSchema - individual schema that defines a component's props and children component keys
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @returns {Node} built component with props and children filled
     */
    buildComponent = (componentSchema, extraProps) => {
        const children = this.buildChildren(componentSchema, extraProps);
        return this.factory.createComponent(componentSchema, extraProps, children);
    };

    /**
     * @desc build children components defined in component schema by calling director
     * @param {Object} componentSchema - individual schema that defines a component's props and children component keys
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @returns {Array|Node|null} list of components or single component or null depend on which builder it goes to
     */
    buildChildren = (componentSchema, extraProps) => {
        const childrenComponentKeys = componentSchema.children || null;
        return childrenComponentKeys && this.props.director.constructComponents(childrenComponentKeys, extraProps);
    };

}

export { ComponentBuilder };
